<template>
  <div class="cal">
    <div class="w-toolbar">
      <div class="w-chips">
        <span class="chip"><b>{{ fmt(total) }}</b> scans in 12 months</span>
        <span class="chip"><b>{{ fmt(activeDays) }}</b> active days</span>
        <span class="chip accent" v-if="busiest"><i class="fas fa-fire"></i> busiest: {{ busiest.label }} ({{ fmt(busiest.scans) }})</span>
      </div>
    </div>
    <div class="cal-wrap">
      <div class="cal-months">
        <span v-for="m in monthMarks" :key="m.key" :style="{ gridColumnStart: m.col + 1 }">{{ m.label }}</span>
      </div>
      <div class="cal-body">
        <div class="cal-days"><span>Mon</span><span></span><span>Wed</span><span></span><span>Fri</span><span></span><span></span></div>
        <div class="cal-grid">
          <span v-for="c in cells" :key="c.key" class="cal-cell" :class="'l' + c.level" :style="{ gridColumnStart: c.col + 1, gridRowStart: c.row + 1, animationDelay: (c.col * 14) + 'ms' }" :title="c.title"></span>
        </div>
      </div>
      <div class="cal-legend">less <span class="cal-cell l0"></span><span class="cal-cell l1"></span><span class="cal-cell l2"></span><span class="cal-cell l3"></span><span class="cal-cell l4"></span> more</div>
    </div>
  </div>
</template>

<script>
import { fmt } from '../theme';

const DAY = 24 * 3600 * 1000;

export default {
  name: 'ScanCalendar',
  props: { stats: { type: Object, required: true } },
  computed: {
    counts() {
      const m = new Map();
      for (const d of (this.stats.insights && this.stats.insights.calendar) || []) m.set(d.day, d.scans);
      return m;
    },
    start() {
      // 53 weeks ending today, starting on a Monday
      const today = new Date();
      today.setUTCHours(0, 0, 0, 0);
      const s = new Date(today.getTime() - 364 * DAY);
      const dow = (s.getUTCDay() + 6) % 7;
      return new Date(s.getTime() - dow * DAY);
    },
    cells() {
      const out = [];
      const end = Date.now();
      const max = Math.max(1, ...this.counts.values());
      for (let t = this.start.getTime(), i = 0; t <= end; t += DAY, i++) {
        const key = new Date(t).toISOString().slice(0, 10);
        const n = this.counts.get(key) || 0;
        const level = n === 0 ? 0 : Math.min(4, Math.ceil((n / max) * 4));
        const label = new Date(t).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
        out.push({ key, col: Math.floor(i / 7), row: i % 7, level, title: `${label}: ${n} scan${n === 1 ? '' : 's'}` });
      }
      return out;
    },
    monthMarks() {
      const marks = [];
      let last = -1;
      for (const c of this.cells) {
        const d = new Date(`${c.key}T00:00:00Z`);
        if (d.getUTCDate() <= 7 && d.getUTCMonth() !== last && c.row === 0) {
          last = d.getUTCMonth();
          marks.push({ key: c.key, col: c.col, label: d.toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' }) });
        }
      }
      return marks;
    },
    total() { let s = 0; for (const v of this.counts.values()) s += v; return s; },
    activeDays() { return this.counts.size; },
    busiest() {
      let best = null;
      for (const [day, scans] of this.counts) if (!best || scans > best.scans) best = { day, scans };
      return best ? { ...best, label: new Date(`${best.day}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }) } : null;
    }
  },
  methods: { fmt }
};
</script>

<style scoped lang="scss">
@import '../widgets.scss';

.cal {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 2px 14px 10px;
}

.cal-wrap {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 4px 4px 0;
  scrollbar-width: thin;
}

.cal-months {
  display: grid;
  grid-template-columns: repeat(53, minmax(10px, 1fr));
  margin-left: 32px;
  font-size: 0.66rem;
  color: var(--text-muted);
  height: 16px;
  min-width: 560px;
}

.cal-body { display: flex; gap: 6px; min-width: 590px; }

.cal-days {
  display: grid;
  grid-template-rows: repeat(7, 1fr);
  width: 26px;
  font-size: 0.6rem;
  color: var(--text-muted);
  align-items: center;
}

.cal-grid {
  flex: 1 1 auto;
  display: grid;
  grid-template-columns: repeat(53, minmax(10px, 1fr));
  grid-template-rows: repeat(7, minmax(10px, 1fr));
  gap: 3px;
}

.cal-cell {
  display: inline-block;
  border-radius: 3px;
  aspect-ratio: 1;
  min-width: 10px;
  background: var(--bg-secondary);
  opacity: 0;
  transform: scale(0.4);
  animation: cal-pop 0.45s cubic-bezier(.2, .8, .2, 1) forwards;
  transition: transform 0.15s;

  &:hover { transform: scale(1.35); outline: 2px solid rgba(var(--accent-green-rgb), 0.5); z-index: 1; }
  &.l1 { background: rgba(var(--accent-green-rgb), 0.3); }
  &.l2 { background: rgba(var(--accent-green-rgb), 0.55); }
  &.l3 { background: rgba(var(--accent-green-rgb), 0.8); }
  &.l4 { background: var(--co2-accent); box-shadow: 0 0 8px rgba(var(--co2-accent-rgb), 0.6); }
}

@keyframes cal-pop { to { opacity: 1; transform: scale(1); } }

.cal-legend {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 3px;
  margin-top: 6px;
  font-size: 0.66rem;
  color: var(--text-muted);

  .cal-cell { width: 11px; min-width: 11px; animation: none; opacity: 1; transform: none; }
}
</style>
